import type { FC } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from "@mui/material";
import { FoodsIcon, MRTGrid } from "@foodpilot/foods";

import {
    allIngredientReferencesKey,
    IngredientReference,
} from "src/api/client-api/ingredientReference/useAllIngredientReferences";
import { useDeleteIngredientReference } from "src/api/client-api/ingredientReference/useDeleteIngredientReference";
import { useSnackbar } from "src/utils/useSnackbar";

import { useIngredientReferenceListContext } from "./useIngredientReferenceListContext";
import { IngredientReferenceModal } from "./IngredientReferenceModal";

export const IngredientReferenceGrid: FC = () => {
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();
    const queryClient = useQueryClient();

    const {
        ingredientReferences,
        isLoading,
        mrt_columns,
        totalItems,
        page,
        setPage,
        sorting,
        setSorting,
        delayedSearch,
    } = useIngredientReferenceListContext();

    const [editing, setEditing] = useState<IngredientReference | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [toDelete, setToDelete] = useState<IngredientReference | null>(null);

    const deleteMutation = useDeleteIngredientReference();

    const handleEditClick = (item: IngredientReference) => {
        setEditing(item);
        setShowEditModal(true);
    };

    const handleDeleteClick = (item: IngredientReference) => {
        setToDelete(item);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!toDelete) return;

        try {
            await deleteMutation.mutateAsync(toDelete.id);
            setSnackbarProps({
                message: t("ingredientReferences.deleteSuccess", "Reference deleted successfully"),
                type: "success",
            });
            queryClient.invalidateQueries(allIngredientReferencesKey);
            setDeleteDialogOpen(false);
            setToDelete(null);
        } catch {
            setSnackbarProps({
                message: t("ingredientReferences.deleteError", "Failed to delete reference"),
                type: "error",
            });
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setToDelete(null);
    };

    return (
        <>
            <MRTGrid
                data={ingredientReferences || []}
                columns={mrt_columns.mrt_columns}
                enableRowActions
                enableRowSelection={false}
                enablePagination
                enableSelectAll={false}
                enableColumnPinning
                displayColumnDefOptions={{
                    "mrt-row-actions": { size: 80, grow: false },
                }}
                renderRowActions={({ row }) => (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Box
                            component="button"
                            onClick={() => handleEditClick(row.original)}
                            sx={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "50%",
                                border: "none",
                                backgroundColor: "transparent",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                "&:hover": { backgroundColor: "#F5F5F5" },
                            }}
                        >
                            <FoodsIcon icon="edit" size={1} />
                        </Box>
                        <Box
                            component="button"
                            onClick={() => handleDeleteClick(row.original)}
                            sx={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "50%",
                                border: "none",
                                backgroundColor: "transparent",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                "&:hover": { backgroundColor: "#F5F5F5" },
                            }}
                        >
                            <FoodsIcon icon="delete" size={1} />
                        </Box>
                    </Stack>
                )}
                state={{
                    isLoading,
                    pagination: page,
                    sorting,
                    globalFilter: delayedSearch,
                    columnVisibility: mrt_columns.columnVisibility,
                    columnOrder: mrt_columns.columnOrder,
                    columnSizing: mrt_columns.columnSizing,
                    columnPinning: {
                        left: [mrt_columns.columnOrder[0]],
                        right: ["mrt-row-actions"],
                    },
                }}
                onColumnSizingChange={mrt_columns.setColumnSizing}
                rowCount={totalItems}
                manualPagination
                onPaginationChange={setPage}
                manualSorting
                onSortingChange={setSorting}
                manualFiltering
                localization={{ actions: "" }}
                muiTableBodyRowProps={{
                    sx: {
                        "&:hover": { backgroundColor: "#F9F9F8" },
                        "& .MuiTableCell-root": {
                            paddingTop: "16px",
                            paddingBottom: "16px",
                        },
                    },
                }}
            />

            <IngredientReferenceModal
                open={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setEditing(null);
                }}
                editing={editing || undefined}
                onSuccess={() => {
                    setShowEditModal(false);
                    setEditing(null);
                    queryClient.invalidateQueries(allIngredientReferencesKey);
                }}
            />

            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: "16px", padding: "8px" } }}
            >
                <DialogTitle
                    sx={{
                        fontWeight: 700,
                        fontSize: "1.125rem",
                        color: "#1E1E1E",
                        padding: "24px 24px 16px",
                    }}
                >
                    {t("ingredientReferences.deleteTitle", "Delete reference")}
                </DialogTitle>
                <DialogContent sx={{ padding: "0 24px 24px" }}>
                    <Typography sx={{ fontSize: "0.875rem", color: "#50504D", lineHeight: "1.6" }}>
                        {t(
                            "ingredientReferences.deleteMessage",
                            "Are you sure you want to delete this ingredient reference?",
                        )}
                        {toDelete && (
                            <strong>
                                {" "}
                                {toDelete.name}
                                {toDelete.code ? ` (${toDelete.code})` : ""}
                            </strong>
                        )}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ padding: "16px 24px 24px", gap: "12px" }}>
                    <Button
                        onClick={handleDeleteCancel}
                        variant="outlined"
                        sx={{
                            borderRadius: "24px",
                            padding: "8px 24px",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            textTransform: "none",
                            borderColor: "#E7E6E4",
                            color: "#393A36",
                            "&:hover": { borderColor: "#D0D0D0", backgroundColor: "#F5F5F5" },
                        }}
                    >
                        {t("common.cancel", "Cancel")}
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        disabled={deleteMutation.isLoading}
                        sx={{
                            borderRadius: "24px",
                            padding: "8px 24px",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            textTransform: "none",
                            backgroundColor: "#D32F2F",
                            "&:hover": { backgroundColor: "#B71C1C" },
                        }}
                    >
                        {deleteMutation.isLoading ?
                            <CircularProgress size={16} color="inherit" />
                        :   t("common.delete", "Delete")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
